import './assets/main.css'

import { ViteSSG } from 'vite-ssg'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'

import App from './App.vue'
import { routes, installGuard } from './router'
import { initTelemetry, capturePageview, capturePageleave } from '@/composables/useTelemetry'
import en from '@/locales/en.json'

const i18n = createI18n({
  legacy: false, // Composition API
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en },
})

export const createApp = ViteSSG(
  App,
  // ViteSSG builds the router itself: createMemoryHistory during SSR
  // (no `window`), createWebHistory on the client.
  { routes, base: import.meta.env.BASE_URL },
  ({ app, router, isClient }) => {
    // Prerender (INC-138): a production Vue app only logs component errors, so a
    // page that throws while prerendering (e.g. PrivacyView's VITE_LEGAL_* guard)
    // would ship broken HTML with exit 0. Rethrow so vite-ssg fails the build.
    if (import.meta.env.SSR) app.config.throwUnhandledErrorInProduction = true
    app.use(createPinia())
    app.use(i18n)
    installGuard(router)

    if (isClient) {
      // Legacy `#/…` invite links (pre history-mode): rewrite the hash into a
      // real path before the router resolves the initial route.
      const { hash } = window.location
      if (hash.startsWith('#/')) {
        window.history.replaceState(null, '', hash.slice(1))
      }

      // Telemetry (INC-098/ADR-053): host-layer only; no-op without PROD +
      // VITE_POSTHOG_KEY (FR-6). SPA route changes are captured via afterEach
      // (FR-4); init is deferred past the initial navigation/mount (NFR-4) and,
      // since init uses `capture_pageview: false`, the initial load is captured
      // exactly once right after init. Since INC-106 the SDK arrives in its own
      // async chunk, so the initial `$pageview` awaits init's resolution.
      router.afterEach((_to, from) => {
        // Emit `$pageleave` for the page we just left before the new `$pageview`.
        // `from.name` is undefined on the very first navigation (no prior page), so
        // the initial load produces a single `$pageview` and no spurious leave.
        if (from.name) capturePageleave(window.location.origin + from.fullPath)
        capturePageview()
      })
      void router.isReady().then(async () => {
        await initTelemetry()
        capturePageview()
      })
    }
  },
)
